import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { fetchJSON, AuthError } from '@/api/client';

export interface IncidentTimeline {
  timestamp: string;
  event_type: string;
  description: string;
  actor?: string;
}

export interface IncidentRCA {
  root_cause: string;
  contributing_factors: string;
  mitigations: string;
  status: string;
}

export interface IncidentDetail {
  id: string;
  severity: string;
  status: string;
  trigger_type: string;
  opened_at: string;
  contained_at?: string;
  resolved_at?: string;
  closed_at?: string;
  rca_status?: string;
  description?: string;
  timeline: IncidentTimeline[];
  rca?: IncidentRCA;
}

export function useIncidentDetail(id: string | null) {
  const { token, logout } = useAuth();
  const [incident, setIncident] = useState<IncidentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => { setReloadKey((k) => k + 1); }, []);

  useEffect(() => {
    if (!token) return;
    if (!id) { setIncident(null); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    fetchJSON<{ incident?: IncidentDetail }>(`/api/incidents/${encodeURIComponent(id)}`, token)
      .then((d) => { if (!cancelled) { setIncident(d.incident || null); setLoading(false); } })
      .catch((e) => {
        if (e instanceof AuthError) { logout(); return; }
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, token, reloadKey, logout]);

  return { incident, loading, reload };
}
